

































































import { Component, Prop, Watch, Emit, Ref, Mixins } from 'vue-property-decorator';
import { generateUID } from '@/utils/utils';
import SSelectOption from './SSelectOption.vue';
import get from 'lodash/get';
import xor from 'lodash/xor';
import SInputBase from '../SInputBase.vue';
import SFormElement from '../SFormElement.vue';
import * as isTabDown from '../../IsTabDown';


type Option<ValueType> = {
  value: ValueType;
  label: string;
  // Shown as a second, aligned column to the right of the label.
  detail?: string;
}

@Component({
  components: {
    SSelectOption,
    SInputBase,
  },
})
export default class SSelect<ValueType, OriginalOption> extends Mixins(SFormElement) {
  @Prop({ required: true }) protected value!: ValueType | ValueType[];
  @Prop({ type: Array, required: true }) private options!: OriginalOption[];
  @Prop({ type: [ String, Function ], default: () => (option: OriginalOption) => option }) valueBy!: string | ((option: OriginalOption) => ValueType);
  @Prop({ type: [ String, Function ], default: () => (option: OriginalOption) => String(option) }) displayBy!: string | ((option: OriginalOption) => string);
  @Prop({ type: [ String, Function ] }) detailBy?: string | ((option: OriginalOption) => string);
  @Prop(Boolean) private disabled?: boolean;
  @Prop(Boolean) private multiple?: boolean;
  @Prop(Boolean) private withEmptyOption?: boolean;
  @Prop({ type: Object, default: () => ({ value: null, label: '' }) }) private emptyOption!: Option<ValueType>;

  private open = false;

  protected get objectValue(): OriginalOption | OriginalOption[] | undefined {
    if (!this.multiple) {
      return this.options.find(o => this.valueFor(o) === (this.selectedOption as Option<ValueType> | undefined)?.value);
    } else {
      return this.options.filter(o => (this.selectedOption as Option<ValueType>[]).some(so => this.valueFor(o) === so.value));
    }
  }

  private toggleOpen(open: boolean) {
    if (this.disabled) return;
    if (this.open === open) return;

    this.open = open;

    if (this.open) this.focusSelectedValue();
  }

  // Options handling

  @Emit('input')
  private optionClicked(option: Option<ValueType>): ValueType | ValueType[] {
    if (!this.multiple) {
      this.toggleOpen(false);
      return option.value;
    } else {
      return xor(this.value as ValueType[], [ option.value ]);
    }
  }

  private get formattedOptions(): Option<ValueType>[] {
    const formattedOptions: Option<ValueType>[] = this.options.map(o => ({
      value: this.valueFor(o),
      label: this.labelFor(o),
      detail: this.detailFor(o),
    }));

    if (this.withEmptyOption) {
      formattedOptions.unshift(this.emptyOption);
    }

    return formattedOptions;
  }

  private valueFor(option: OriginalOption): ValueType {
    if (typeof this.valueBy === 'function') return this.valueBy(option);

    return get(option, this.valueBy) as ValueType;
  }

  private labelFor(option: OriginalOption): string {
    if (typeof this.displayBy === 'function') return this.displayBy(option);

    return String(get(option, this.displayBy));
  }

  private detailFor(option: OriginalOption): string | undefined {
    if (this.detailBy === undefined) return undefined;
    if (typeof this.detailBy === 'function') return this.detailBy(option);

    return String(get(option, this.detailBy));
  }

  private get selectedOption(): Option<ValueType> | undefined | Option<ValueType>[] {
    if (!this.multiple) {
      return this.formattedOptions.find(o => this.isOptionSelected(o));
    } else {
      return this.formattedOptions.filter(o => this.isOptionSelected(o));
    }
  }

  private isOptionSelected(option: Option<ValueType>): boolean {
    if (!this.multiple) {
      return option.value === (this.value as ValueType);
    } else {
      return (this.value as ValueType[]).includes(option.value);
    }
  }

  // SInput internals

  private get listeners() {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { input, ...listeners } = this.$listeners;
    return listeners;
  }

  private get inputText(): string {
    if (this.multiple) {
      return (this.selectedOption as Option<ValueType>[]).map(o => o.label).join(', ');
    } else {
      if (!this.selectedOption) return '';

      return (this.selectedOption as Option<ValueType>).label;
    }
  }

  // Focus

  private focusedIndex = 0;

  private focusSelectedValue(): void {
    if (!this.multiple) {
      this.focusedIndex = this.selectedOption ? this.formattedOptions.indexOf(this.selectedOption as Option<ValueType>) : 0;
    } else {
      this.focusedIndex = (this.selectedOption as Option<ValueType>[]).length ? this.formattedOptions.indexOf((this.selectedOption as Option<ValueType>[])[0]) : 0;
    }
  }

  private changeFocus(step: number): void {
    this.focusedIndex += step;

    if (this.focusedIndex >= this.formattedOptions.length) this.focusedIndex = 0;
    if (this.focusedIndex < 0) this.focusedIndex = this.formattedOptions.length - 1;
  }

  private clickFocusedOption(): void {
    this.optionClicked(this.formattedOptions[this.focusedIndex]);
  }

  // Styles

  @Prop({ type: Boolean, default: true }) private fullSize!: boolean;
  @Prop(Boolean) private inline?: boolean;
  @Ref() private readonly inputRef!: SInputBase;

  private optionsHolderStyle = {
    left: '0',
    top: '0',
    minWidth: '0',
    maxWidth: '200px',
  }

  // Polling is added to fix the position of select dropdown,
  // in case the select input moves (example: scroll, popup changed position).
  private positionPoll: NodeJS.Timeout | null = null;

  @Watch('open')
  private positionPolling(): void {
    if (this.open) {
      this.updatePosition();
      this.positionPoll = setInterval(this.updatePosition, 25);
    } else if (this.positionPoll) {
      // when select is present in DOM but menu is not open
      // we need to clear the polling
      clearInterval(this.positionPoll);
      this.positionPoll = null;
    }
  }

  public destroyed() {
    // when select is destroyed interval clearing in positionPolling will not happen
    // example of this would be Select in popup
    if (this.positionPoll) clearInterval(this.positionPoll);
    this.tabDownSubscription.unsubscribe();
  }

  private updatePosition(): void {
    const inputRect = (this.inputRef.inputHolderRef.getBoundingClientRect() as DOMRect);
    this.optionsHolderStyle.top = `${inputRect.y + inputRect.height + (window.pageYOffset || document.documentElement.scrollTop)}px`;
    this.optionsHolderStyle.left = `${inputRect.x + (window.pageXOffset || document.documentElement.scrollLeft)}px`;
    this.optionsHolderStyle.minWidth = `${inputRect.width}px`;
    this.optionsHolderStyle.maxWidth = `${Math.max(inputRect.width, 200)}px`;
  }

  // Handle clicking outside select

  private uid = generateUID();

  private handleClickOutsideSelect(event: Event) {
    const path = event.composedPath() as HTMLElement[];
    if (path.some((el: HTMLElement) => el.classList && Array.from(el.classList).includes(`js-select-${this.uid}`))) return;
    this.toggleOpen(false);
  }

  // Handle tabbing in

  @Prop({type: Boolean, default: true}) private openOnTabIn!: boolean;

  private tabDownSubscription!: isTabDown.Subscription;
  private isTabDown = false;

  private mounted() {
    this.tabDownSubscription = isTabDown.subscribe(value => this.isTabDown = value);
  }
}
